import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { Plus, Search, Trash2, Pencil, Building2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  name: "", company: "", email: "", phone: "", address: "",
  gstin: "", customer_type: "commercial", notes: "",
};

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState(null);

  const load = async () => {
    const r = await api.get("/customers");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return [r.name, r.company, r.email, r.phone, r.address].filter(Boolean).some((x) => x.toLowerCase().includes(t));
  });

  const save = async (data) => {
    const payload = { ...data, email: data.email || null };
    try {
      if (drawer.mode === "edit") await api.patch(`/customers/${drawer.data.id}`, payload);
      else await api.post("/customers", payload);
      toast.success("Saved");
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete customer?")) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        subtitle="Your accounts and contacts."
        actions={
          <button className="btn-primary flex items-center gap-2" data-testid="add-customer-btn"
            onClick={() => setDrawer({ mode: "create", data: empty })}>
            <Plus className="w-4 h-4" /> New Customer
          </button>
        }
      />

      <div className="crm-card p-4 mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-base pl-9" placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((r) => (
          <div key={r.id} className="crm-card p-4" data-testid={`customer-card-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-semibold text-slate-900">{r.name}</div>
                <div className="text-xs text-slate-500">{r.company || "—"}</div>
                <span className="badge bg-slate-100 text-slate-600 mt-2 capitalize">{r.customer_type}</span>
              </div>
              <div className="flex gap-1">
                <button className="btn-ghost !p-1.5" onClick={() => setDrawer({ mode: "edit", data: { ...empty, ...r } })} data-testid={`edit-customer-${r.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button className="btn-ghost !p-1.5 text-red-600" onClick={() => remove(r.id)} data-testid={`delete-customer-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-1 text-sm text-slate-600">
              {r.email && <div>{r.email}</div>}
              {r.phone && <div>{r.phone}</div>}
              {r.address && <div className="text-xs text-slate-500 line-clamp-2">{r.address}</div>}
              {r.gstin && <div className="text-xs font-mono text-slate-500">GSTIN: {r.gstin}</div>}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
              Added {fmtDate(r.created_at)}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full crm-card p-10 text-center text-slate-500">
            <Building2 className="w-6 h-6 mx-auto mb-2 opacity-50" />
            No customers yet.
          </div>
        )}
      </div>

      {drawer && (
        <Drawer open onClose={() => setDrawer(null)} title={drawer.mode === "edit" ? "Edit Customer" : "New Customer"}
          testid="customer-drawer"
          footer={
            <>
              <button className="btn-outline" onClick={() => setDrawer(null)}>Cancel</button>
              <button className="btn-primary" data-testid="customer-save-btn" onClick={() => save(drawer.data)}>Save</button>
            </>
          }
        >
          <CustomerForm data={drawer.data} onChange={(d) => setDrawer({ ...drawer, data: d })} />
        </Drawer>
      )}
    </div>
  );
}

function CustomerForm({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Name *"><input className="input-base" value={data.name} data-testid="customer-name" onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Company"><input className="input-base" value={data.company || ""} onChange={(e) => set("company", e.target.value)} /></Field>
        <Field label="Email"><input className="input-base" type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Phone"><input className="input-base" value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="Type">
          <select className="input-base" value={data.customer_type} onChange={(e) => set("customer_type", e.target.value)}>
            {["residential", "commercial", "industrial", "utility"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="GSTIN"><input className="input-base" value={data.gstin || ""} onChange={(e) => set("gstin", e.target.value)} /></Field>
      </div>
      <Field label="Address"><textarea className="input-base min-h-[70px]" value={data.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
      <Field label="Notes"><textarea className="input-base min-h-[70px]" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
    </>
  );
}
